
(function () {
  'use strict';

  function initStickyHeader(header) {
    var offset = parseInt(header.dataset.stickyOffset, 10) || 0;
    var ticking = false;

    function update() {
      var scrollY = window.scrollY || document.documentElement.scrollTop || 0;
      header.classList.toggle('is-scrolled', scrollY > offset);
      ticking = false;
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    update(); // set correct state on load (e.g. page refreshed mid-scroll)
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
  }

  function initMenuPanel(header) {
    var btn = header.querySelector('#header-v2-menu-btn');
    var panel = header.querySelector('#header-v2-menu-panel');
    if (!btn || !panel) return;

    function close() {
      panel.classList.remove('is-open');
      btn.setAttribute('aria-expanded', 'false');
    }

    function open() {
      panel.classList.add('is-open');
      btn.setAttribute('aria-expanded', 'true');
    }

    btn.addEventListener('click', function (event) {
      event.stopPropagation();
      if (panel.classList.contains('is-open')) {
        close();
      } else {
        open();
      }
    });

    document.addEventListener('click', function (event) {
      if (!header.contains(event.target)) close();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') close();
    });
  }

  // ---------------------------------------------------------------------------
  // Adventure section: angle viewer. Each colourway is a set of pre-rendered
  // cut-outs (front, front 3/4, back 3/4, back) stacked in the stage; dragging
  // walks the set so it reads as one continuous turn.
  //
  // Every frame of every colourway is already in the DOM as an <img>, so a drag
  // or a colour change is a class toggle with nothing to fetch. Only the first
  // frame is eager; the rest are loading="lazy" and arrive as the section nears
  // the viewport.
  //
  // Deliberately not a 3D model: <model-viewer> cost ~1MB of library plus 1.4MB
  // per .glb, and only ever showed these same angles.
  // ---------------------------------------------------------------------------

  function initAdventureViewer(section) {
    var stage = section.querySelector('[data-tuko-stage]');
    var hint = section.querySelector('[data-tuko-hint]');
    var nameEl = section.querySelector('[data-tuko-colorway-name]');
    var swatches = [].slice.call(section.querySelectorAll('.tuko-adventure__swatch'));
    var frames = stage ? [].slice.call(stage.querySelectorAll('[data-tuko-frame]')) : [];

    if (!stage) return;

    // Frames are laid out colourway-major in the markup, so index arithmetic is
    // enough — no need to query per change.
    var frameCount = parseInt(stage.dataset.frameCount, 10) || 0;
    var colorwayCount = frameCount ? frames.length / frameCount : 0;
    var dragStep = parseInt(stage.dataset.dragStep, 10) || 55;

    var frame = 0;
    var colorway = 0;

    function show() {
      if (!frameCount) return;

      var active = colorway * frameCount + frame;
      frames.forEach(function (img, index) {
        img.classList.toggle('is-shown', index === active);
      });
    }

    // --- Colourway ----------------------------------------------------------

    function selectColorway(index, focus) {
      var swatch = swatches[index];
      if (!swatch) return;

      swatches.forEach(function (other, otherIndex) {
        var isActive = otherIndex === index;
        other.classList.toggle('is-active', isActive);
        other.setAttribute('aria-checked', isActive ? 'true' : 'false');
        other.tabIndex = isActive ? 0 : -1;
      });

      if (nameEl && swatch.dataset.label) nameEl.textContent = swatch.dataset.label;
      if (focus) swatch.focus();

      // The angle carries over: the visitor keeps looking at the same side.
      if (index < colorwayCount) {
        colorway = index;
        show();
      }
    }

    swatches.forEach(function (swatch, index) {
      swatch.addEventListener('click', function () {
        selectColorway(index);
      });

      // Arrow keys move between radios, matching native radio group behaviour.
      swatch.addEventListener('keydown', function (event) {
        var step = 0;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') step = 1;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') step = -1;
        if (!step) return;
        event.preventDefault();
        selectColorway((index + step + swatches.length) % swatches.length, true);
      });
    });

    if (!frames.length || !frameCount) return;

    // --- Angle --------------------------------------------------------------

    function setFrame(index) {
      // Wraps, so a drag past the last angle comes back round to the front.
      frame = ((index % frameCount) + frameCount) % frameCount;
      show();
    }

    function markTouched() {
      if (hint) hint.classList.add('is-hidden');
    }

    // --- Drag / swipe -------------------------------------------------------

    var dragging = false;
    var startX = 0;
    var startFrame = 0;
    var pointerId = null;

    stage.addEventListener('pointerdown', function (event) {
      if (event.button !== undefined && event.button !== 0) return;
      dragging = true;
      startX = event.clientX;
      startFrame = frame;
      pointerId = event.pointerId;
      if (stage.setPointerCapture) stage.setPointerCapture(pointerId);
      markTouched();
    });

    stage.addEventListener('pointermove', function (event) {
      if (!dragging) return;
      setFrame(startFrame + Math.round((event.clientX - startX) / dragStep));
    });

    function endDrag() {
      if (!dragging) return;
      dragging = false;
      if (pointerId !== null && stage.hasPointerCapture && stage.hasPointerCapture(pointerId)) {
        stage.releasePointerCapture(pointerId);
      }
      pointerId = null;
    }

    stage.addEventListener('pointerup', endDrag);
    stage.addEventListener('pointercancel', endDrag);

    // Native image drag would hijack the gesture on desktop.
    stage.addEventListener('dragstart', function (event) {
      event.preventDefault();
    });

    stage.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowRight') {
        setFrame(frame + 1);
        markTouched();
        event.preventDefault();
      }
      if (event.key === 'ArrowLeft') {
        setFrame(frame - 1);
        markTouched();
        event.preventDefault();
      }
    });

    show();
  }

  function initAdventureRows(section) {
    var rows = [].slice.call(section.querySelectorAll('.tuko-adventure__frow'));
    rows.forEach(function (row) {
      row.addEventListener('mouseenter', function () {
        row.classList.add('is-active');
      });
      row.addEventListener('mouseleave', function () {
        row.classList.remove('is-active');
      });
      row.addEventListener('focusin', function () {
        row.classList.add('is-active');
      });
      row.addEventListener('focusout', function () {
        row.classList.remove('is-active');
      });
    });
  }

  function AccordionItem(el) {
    this.el = el;
    this.summary = el.querySelector('summary');
    this.body = el.querySelector('.custom-protect__item-body, .custom-faq__item-body');
    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;

    var self = this;
    this.summary.addEventListener('click', function (event) {
      event.preventDefault();
      self.onClick();
    });
  }

  AccordionItem.prototype.onClick = function () {
    this.el.style.overflow = 'hidden';
    if (this.isClosing || !this.el.open) {
      this.expand();
    } else if (this.isExpanding || this.el.open) {
      this.shrink();
    }
  };

  AccordionItem.prototype.shrink = function () {
    var self = this;
    this.isClosing = true;

    var startHeight = this.el.offsetHeight + 'px';

    // Measure the true collapsed height (item's own padding/border + summary only,
    // with the body's contribution removed) rather than assuming what it should be.
    this.body.style.display = 'none';
    var endHeight = this.el.offsetHeight + 'px';
    this.body.style.display = '';

    if (this.animation) this.animation.cancel();
    this.animation = this.el.animate(
      { height: [startHeight, endHeight] },
      { duration: 250, easing: 'ease-in-out' }
    );
    this.animation.onfinish = function () {
      self.onAnimationFinish(false);
    };
    this.animation.oncancel = function () {
      self.isClosing = false;
    };
  };

  AccordionItem.prototype.expand = function () {
    var self = this;
    this.el.style.height = this.el.offsetHeight + 'px';
    this.el.open = true;
    window.requestAnimationFrame(function () {
      self.grow();
    });
  };

  AccordionItem.prototype.grow = function () {
    var self = this;
    this.isExpanding = true;

    var startHeight = this.el.offsetHeight + 'px';

    // Measure the true expanded height (item's own padding/border + summary + body)
    // by briefly letting the box size naturally, then restore before animating so
    // there's no visible flash of the end state.
    this.el.style.height = 'auto';
    var endHeight = this.el.offsetHeight + 'px';
    this.el.style.height = startHeight;

    if (this.animation) this.animation.cancel();
    this.animation = this.el.animate(
      { height: [startHeight, endHeight] },
      { duration: 250, easing: 'ease-in-out' }
    );
    this.animation.onfinish = function () {
      self.onAnimationFinish(true);
    };
    this.animation.oncancel = function () {
      self.isExpanding = false;
    };
  };

  AccordionItem.prototype.onAnimationFinish = function (open) {
    this.el.open = open;
    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;
    this.el.style.height = '';
    this.el.style.overflow = '';
  };

  function initAccordion(section, itemSelector) {
    var items = [].slice.call(section.querySelectorAll(itemSelector));
    items.forEach(function (el) {
      new AccordionItem(el);
    });
  }

  function initSlider(section) {
    var slides = [].slice.call(section.querySelectorAll('.custom-slider__slide'));
    var dots = [].slice.call(section.querySelectorAll('.custom-slider__dot'));
    var prevBtn = section.querySelector('.custom-slider__nav--prev');
    var nextBtn = section.querySelector('.custom-slider__nav--next');
    if (slides.length < 2) return;

    var count = slides.length;
    var index = 0;

    function goTo(i) {
      index = ((i % count) + count) % count;
      slides.forEach(function (slide, slideIndex) {
        slide.classList.toggle('is-active', slideIndex === index);
      });
      dots.forEach(function (dot, dotIndex) {
        dot.classList.toggle('is-active', dotIndex === index);
      });
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        goTo(index - 1);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        goTo(index + 1);
      });
    }

    dots.forEach(function (dot, dotIndex) {
      dot.addEventListener('click', function () {
        goTo(dotIndex);
      });
    });
  }

  function initRoadTimeline(section) {
    var steps = [].slice.call(section.querySelectorAll('.custom-road__tstep'));
    if (!steps.length) return;

    var ticking = false;

    function update() {
      var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      var sectionRect = section.getBoundingClientRect();

      // Steps only reset in bulk once the whole section has scrolled below the
      // viewport — i.e. the user scrolled back up past it. Scrolling down past
      // the section (it sits above the viewport) leaves every step as-is.
      if (sectionRect.top > viewportHeight) {
        steps.forEach(function (step) {
          step.classList.remove('is-active');
        });
      } else {
        steps.forEach(function (step) {
          var rect = step.getBoundingClientRect();
          if (rect.top <= viewportHeight * 0.6) {
            step.classList.add('is-active');
          }
        });
      }

      ticking = false;
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    update(); // set correct state on load (e.g. page refreshed mid-scroll)
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    steps.forEach(function (step) {
      step.addEventListener('mouseenter', function () {
        step.classList.add('is-active');
      });
    });
  }

  function initJoinMasonry(section) {
    var container = section.querySelector('.custom-join__bento');
    if (!container) return;

    var cells = [].slice.call(container.querySelectorAll('.custom-join__cell'));
    if (!cells.length) return;

    var resizeTimer = null;

    function columnCountFor() {
      return window.innerWidth <= 1024 ? 1 : 3;
    }

    // True masonry: each cell goes into whichever column is currently
    // shortest, so columns settle at different heights based on real content
    // — not CSS multi-column, which balances total height and just looks
    // like a static grid when cells are similar sizes.
    function layout() {
      var columnCount = columnCountFor();
      container.innerHTML = '';

      if (columnCount <= 1) {
        container.classList.remove('has-columns');
        cells.forEach(function (cell) {
          container.appendChild(cell);
        });
        return;
      }

      container.classList.add('has-columns');

      var columns = [];
      for (var i = 0; i < columnCount; i++) {
        var col = document.createElement('div');
        col.className = 'custom-join__col';
        container.appendChild(col);
        columns.push(col);
      }

      cells.forEach(function (cell) {
        var shortest = columns[0];
        columns.forEach(function (col) {
          if (col.offsetHeight < shortest.offsetHeight) shortest = col;
        });
        shortest.appendChild(cell);
      });
    }

    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(layout, 150);
    });

    layout();
  }

  function initHeroPopup(section) {
    var trigger = section.querySelector('.js-hero-popup-trigger');
    var dialog = section.querySelector('.js-hero-popup');
    if (!trigger || !dialog) return;

    var closeBtn = dialog.querySelector('.js-hero-popup-close');
    var video = dialog.querySelector('.js-hero-popup-video');
    var supportsDialog = typeof dialog.showModal === 'function';

    function openPopup() {
      if (supportsDialog) {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
      if (video) {
        try {
          video.currentTime = 0;
        } catch (err) {
          // ignore - media not ready yet
        }
        var playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(function () {
            // Autoplay with sound can be blocked by the browser; the user
            // still has visible controls to press play themselves.
          });
        }
      }
    }

    function closePopup() {
      if (video) video.pause();
      if (supportsDialog) {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
      }
    }

    trigger.addEventListener('click', function (event) {
      event.preventDefault();
      openPopup();
    });

    if (closeBtn) {
      closeBtn.addEventListener('click', function (event) {
        event.preventDefault();
        closePopup();
      });
    }

    // Click on the ::backdrop area lands on the <dialog> element itself
    // (its inner content is a separate child), so this closes on outside click.
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) closePopup();
    });

    dialog.addEventListener('cancel', function () {
      if (video) video.pause();
    });
  }

  function initScrollReveal(section, itemSelector) {
    var items = [].slice.call(section.querySelectorAll(itemSelector));
    if (!items.length) return;

    if (!('IntersectionObserver' in window)) {
      // No IntersectionObserver support - just show everything rather than
      // leaving content permanently hidden.
      items.forEach(function (item) {
        item.classList.add('is-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -10% 0px' }
    );

    items.forEach(function (item) {
      observer.observe(item);
    });
  }

  // Scroll reveal for [data-aos] elements — a small stand-in for the AOS
  // library with the same attribute API and none of the payload. See
  // _aos.scss for the effects and for why custom-protect and custom-road are
  // left on their own bespoke reveals.
  //
  // Stamping data-aos-ready on <html> is what tells the bootstrap in
  // theme.liquid that this file is alive; without it the bootstrap strips
  // .js-aos after 1.5s and everything renders unanimated but visible.
  //
  // Scroll position rather than IntersectionObserver, matching
  // initRoadTimeline below. IO looks like the obvious tool here and it is the
  // wrong one: a single instantaneous jump — an in-page anchor, a restored
  // scroll position on reload, a hard flick on a phone — can take an element
  // from below the viewport to above it in one frame. Its intersection ratio
  // is 0 before and 0 after, so the state never changes, so IO never fires,
  // and that section stays invisible for the rest of the visit. Comparing
  // rects catches anything at or above the trigger line, including things
  // already scrolled past.
  function initScrollAnimations(root) {
    document.documentElement.setAttribute('data-aos-ready', '');

    var pending = [].slice.call((root || document).querySelectorAll('[data-aos]'));
    if (!pending.length) return;

    var reduceMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion) {
      pending.forEach(function (item) {
        item.classList.add('aos-animate');
      });
      return;
    }

    pending.forEach(function (item) {
      var delay = parseInt(item.getAttribute('data-aos-delay'), 10);
      if (delay > 0) item.style.transitionDelay = delay + 'ms';

      var duration = parseInt(item.getAttribute('data-aos-duration'), 10);
      if (duration > 0) item.style.transitionDuration = duration + 'ms';
    });

    var ticking = false;

    function update() {
      var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      var trigger = viewportHeight * 0.9; // reveal once the top edge is 10% in

      // Backwards, because revealed items are spliced out of the list.
      for (var i = pending.length - 1; i >= 0; i--) {
        if (pending[i].getBoundingClientRect().top > trigger) continue;
        pending[i].classList.add('aos-animate');
        pending.splice(i, 1);
      }

      // Everything has played once; stop reading rects on every scroll frame.
      if (!pending.length) {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
      }

      ticking = false;
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    // Two frames before the first pass, so the hidden starting state is
    // actually painted first. Called synchronously, anything already in view
    // would get its .aos-animate in the same frame as its opacity: 0 and snap
    // in with no transition at all.
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(update);
    });

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
  }

  function initTeamCards(section) {
    var toggles = [].slice.call(section.querySelectorAll('.custom-team__toggle'));
    toggles.forEach(function (toggle) {
      var card = toggle.closest('.custom-team__card');
      var wrap = card ? card.querySelector('.custom-team__bio-wrap') : null;
      if (!wrap) return;

      toggle.addEventListener('click', function () {
        var isOpen = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
        wrap.classList.toggle('is-open', !isOpen);
      });
    });
  }

  function initReservePopup() {
    var popup = document.getElementById('reserve-popup');
    if (!popup) return;

    var triggers = document.querySelectorAll(
      '.header-v2__cta, .custom-hero__cta:not(.custom-hero__cta--ghost), .custom-closing__cta, .wc-float-join, .wc-sticky-waitlist__cta, .custom-countdown__cta, .custom-video-showcase__cta, .custom-protect__cta, .custom-fsvideo__cta, .custom-pricesteps__cta, .custom-road__cta, .custom-golf__cta'
    );
    if (!triggers.length) return;

    var supportsDialog = typeof popup.showModal === 'function';

    function openPopup() {
      if (supportsDialog) {
        popup.showModal();
      } else {
        popup.setAttribute('open', '');
      }
    }

    function closePopup() {
      if (supportsDialog) {
        popup.close();
      } else {
        popup.removeAttribute('open');
      }
    }

    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function (event) {
        // These CTAs point to href="#" — without preventDefault the browser
        // treats the click as a same-page anchor jump and scrolls to the top
        // before the popup even opens. This stops that.
        event.preventDefault();
        // First click on a "Become a Founding Member" button: custom event (the Lead fires after signup).
        if (window.fbq) {
          try { window.fbq('trackCustom', 'BecomeFoundingMember', { button: (trigger.className || '').toString().split(' ')[0] }); } catch (err) {}
        }
        showPopupState(popup);
        openPopup();
      });
    });

    var closeBtn = popup.querySelector('.reserve-popup__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', function (event) {
        event.preventDefault();
        closePopup();
      });
    }

    // Click on the ::backdrop area lands on the <dialog> element itself
    // (its inner content is a separate child), so this closes on outside click.
    popup.addEventListener('click', function (event) {
      if (event.target === popup) closePopup();
    });
  }

  // ---- Founding-list signup ------------------------------------------------------------
  // All signup spots are the same Klaviyo embedded form. When one is submitted we remember that
  // the visitor joined and show a short "you're in" message in the popup, which closes by itself
  // after a few seconds; from then on the popup says "you're on the list" instead of the form.
  // While the signup is being tested, visitors can keep re-entering their email: set to true to
  // make the popup say "you're on the list" for people who already joined.
  var SS_REMEMBER_JOINED = false;

  function ssJoined() {
    if (!SS_REMEMBER_JOINED) return false;
    try { return localStorage.getItem('ss_joined') === '1'; } catch (e) { return false; }
  }

  // Popup content: the signup form, or the "you're on the list" message for people who joined.
  function showPopupState(popup) {
    if (ssJoined()) { renderJoinedPopup(popup); return; }
    var wrap = popup.querySelector('.reserve-popup__form');
    if (!wrap) return;
    wrap.classList.remove('is-joined');
    var old = wrap.querySelector('.ss-joined');
    if (old) old.remove();
  }

  function renderJoinedPopup(popup, headline) {
    var wrap = popup.querySelector('.reserve-popup__form');
    if (!wrap) return;
    var old = wrap.querySelector('.ss-joined');
    if (old) old.remove();
    // Hide (rather than remove) the form so it can come back, still rendered by Klaviyo.
    wrap.classList.add('is-joined');
    wrap.insertAdjacentHTML('beforeend',
      '<div class="ss-joined">' +
      (popup.getAttribute('data-logo') ? '<img class="ss-joined__logo" src="' + popup.getAttribute('data-logo') + '" alt="SeatStix" width="920" height="739">' : '') +
      '<span class="ss-joined__icon"><svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg></span>' +
      '<h2 class="reserve-popup__title ss-joined__title">' + (headline || 'You\'re on the list.') + '</h2>' +
      '<p class="reserve-popup__sub ss-joined__sub">Keep an eye on your emails for the latest updates.</p>' +
      (headline ? '<span class="ss-joined__bar"><i></i></span>' : '') +
      '</div>');
  }

  function initSignupForms() {
    if (window.top !== window) return;

    var thanksTimer = null;

    // Show the "you're in" popup, then close it by itself after a few seconds.
    function showThanks() {
      var popup = document.getElementById('reserve-popup');
      if (!popup) return;
      renderJoinedPopup(popup, 'You\'re in. Welcome to the founding list.');
      if (typeof popup.showModal === 'function') { if (!popup.open) popup.showModal(); } else { popup.setAttribute('open', ''); }
      clearTimeout(thanksTimer);
      thanksTimer = setTimeout(function () {
        if (typeof popup.showModal === 'function') { popup.close(); } else { popup.removeAttribute('open'); }
        showPopupState(popup);
      }, 5000);
    }

    // Signups are Klaviyo embedded forms (klaviyo-form-XaeKzJ). Klaviyo fires this event when
    // one is submitted.
    window.addEventListener('klaviyoForms', function (e) {
      if (!e.detail || e.detail.type !== 'submit') return;
      try { localStorage.setItem('ss_joined', '1'); } catch (err) {}
      // Signup went through: standard Lead event (the one to optimise ads for).
      if (window.fbq) {
        try { window.fbq('track', 'Lead', { content_name: 'Become a Founding Member' }); } catch (err) {}
      }
      // Swap straight to our thank-you so Klaviyo's own success step never shows. The form is only
      // hidden (not removed), so Klaviyo can still finish sending.
      showThanks();
    });
  }

  function initFloatLogoContrast() {
    var logo = document.querySelector('.wc-float-logo');
    var sections = [
      document.getElementById('custom-hero'),
      document.getElementById('custom-closing'),
      document.querySelector('.custom-countdown')
    ].filter(Boolean);
    if (!logo || !sections.length || typeof IntersectionObserver === 'undefined') return;

    var intersecting = new Set();

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            intersecting.add(entry.target);
          } else {
            intersecting.delete(entry.target);
          }
        });
        logo.classList.toggle('wc-float-logo--light', intersecting.size > 0);
      },
      // Only count a section as "under the logo" if it covers the
      // logo's own corner (top-left, roughly the top 150px of the
      // viewport) -- otherwise a section's photo merely peeking in at
      // the opposite edge of the screen (e.g. the next section's top
      // sliver scrolling into view at the bottom) would wrongly force
      // the logo white while it's actually sitting over a plain white
      // section in between.
      { threshold: 0, rootMargin: '0px 0px -85% 0px' }
    );
    sections.forEach(function (section) {
      observer.observe(section);
    });

    // The video-showcase section's cards sit directly under the logo's
    // corner and the logo would otherwise overlap "Superior Comfort" as
    // the page scrolls past -- just hide it for that section instead of
    // trying to recolor it against a white card background.
    var hideSection = document.getElementById('custom-video-showcase');
    if (hideSection) {
      var hideObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            logo.classList.toggle('wc-float-logo--hidden', entry.isIntersecting);
          });
        },
        { threshold: 0, rootMargin: '0px 0px -85% 0px' }
      );
      hideObserver.observe(hideSection);
    }
  }

  // Phones: the floating logo and pill sit on top of headings once the hero
  // is gone (the sticky bottom bar covers the CTA from there), so flag the
  // body and let CSS fade them out below the hero.
  function initMobileFloatHide() {
    var hero = document.getElementById('custom-hero');
    if (!hero || typeof IntersectionObserver === 'undefined') return;
    new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          document.body.classList.toggle('wc-past-hero', !entry.isIntersecting);
        });
      },
      { threshold: 0 }
    ).observe(hero);
  }

  function initStickyWaitlist() {
    var bar = document.getElementById('wc-sticky-waitlist');
    // Watch the whole hero section, not just its CTA row -- the bar should
    // stay hidden for as long as any part of the hero (video, its own
    // buttons, etc.) is still on screen, only appearing once it's fully
    // scrolled past. Using the CTA row alone let the sticky bar appear
    // while the video was still visible, duplicating the hero's own CTAs.
    // Both the hero and the video-showcase chapter right after it carry
    // their own "Join the List" CTA, so the sticky bar should stay hidden
    // through both -- otherwise it overlaps the second chapter's own
    // badge/button with a redundant "Join the Waitlist" right on top of it.
    var heroSection = document.getElementById('custom-video-showcase') || document.getElementById('custom-hero');
    if (!bar || !heroSection || typeof IntersectionObserver === 'undefined') return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          bar.classList.toggle('is-visible', !entry.isIntersecting);
        });
      },
      { threshold: 0 }
    );
    observer.observe(heroSection);
  }

  function initCountdowns() {
    var els = document.querySelectorAll('[data-countdown-target]');
    if (!els.length) return;

    function paint() {
      els.forEach(function (el) {
        var target = new Date(el.getAttribute('data-countdown-target')).getTime();
        var diff = Math.max(0, target - Date.now());

        var d = Math.floor(diff / 86400000);
        var h = Math.floor((diff % 86400000) / 3600000);
        var m = Math.floor((diff % 3600000) / 60000);
        var s = Math.floor((diff % 60000) / 1000);
        var values = { d: d, h: h, m: m, s: s };

        el.querySelectorAll('[data-unit]').forEach(function (numEl) {
          var unit = numEl.getAttribute('data-unit');
          if (values[unit] === undefined) return;
          numEl.textContent = unit === 'd' ? String(values[unit]) : String(values[unit]).padStart(2, '0');
        });
      });
    }

    paint();
    setInterval(paint, 1000);
  }

  function init() {
    initScrollAnimations();
    initStickyWaitlist();
    initMobileFloatHide();
    initCountdowns();

    document.querySelectorAll('.header-v2').forEach(function (header) {
      if (header.classList.contains('header-v2--sticky')) {
        initStickyHeader(header);
      }
      initMenuPanel(header);
    });

    document.querySelectorAll('.tuko-adventure').forEach(function (section) {
      initAdventureViewer(section);
      initAdventureRows(section);
    });

    document.querySelectorAll('.custom-hero').forEach(function (section) {
      initHeroPopup(section);
    });

    initReservePopup();
    initSignupForms();
    initFloatLogoContrast();

    document.querySelectorAll('.custom-protect').forEach(function (section) {
      initScrollReveal(section, '.js-scroll-reveal');
    });

    document.querySelectorAll('.custom-faq').forEach(function (section) {
      initAccordion(section, '.custom-faq__item');
    });

    document.querySelectorAll('.custom-slider').forEach(function (section) {
      initSlider(section);
    });

    document.querySelectorAll('.custom-road').forEach(function (section) {
      initRoadTimeline(section);
    });

    document.querySelectorAll('.custom-join').forEach(function (section) {
      initJoinMasonry(section);
    });

    document.querySelectorAll('.custom-team').forEach(function (section) {
      initTeamCards(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor swaps out a whole section's markup when a merchant edits
  // it. Its [data-aos] elements arrive hidden and unobserved, so without this
  // the section they are editing would disappear on them.
  document.addEventListener('shopify:section:load', function (event) {
    initScrollAnimations(event.target);
  });
})();
